import {ContentContainer} from '@coolcolduk/ui-mui';
import RefreshIcon from '@mui/icons-material/Refresh';
import {Button, Card, CardContent, CircularProgress, Typography} from '@mui/material';
import {useCallback, useEffect, useState} from 'react';
import {ProtectedRouteChildrenProps} from '../../../components/template/ProtectedRoute';
import {firebaseAuth} from '../../../config/firebase';

interface PermissionSettingsPageProps extends ProtectedRouteChildrenProps {}

function formatJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export default function PermissionSettingsPage({user}: PermissionSettingsPageProps) {
  const [claims, setClaims] = useState<Record<string, unknown> | null>(null);
  const [claimsLoading, setClaimsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadClaims = useCallback(async () => {
    const currentUser = firebaseAuth.currentUser;
    if (!currentUser) {
      setClaims(null);
      setClaimsLoading(false);
      return;
    }
    try {
      const tokenResult = await currentUser.getIdTokenResult();
      setClaims((tokenResult.claims as Record<string, unknown>) ?? null);
    } catch {
      setClaims(null);
    } finally {
      setClaimsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadClaims();
  }, [loadClaims]);

  const handleRefreshTokenClaims = useCallback(async () => {
    const currentUser = firebaseAuth.currentUser;
    if (!currentUser) return;
    setRefreshing(true);
    try {
      await currentUser.getIdToken(true);
      await loadClaims();
    } finally {
      setRefreshing(false);
    }
  }, [loadClaims]);

  return (
    <ContentContainer data-testid="permission-settings-page">
      <Typography variant="h6" component="h2" sx={{mb: 2}}>
        Your permissions
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
        Token claims (from Firebase Auth) and your user record (from Firestore). Read-only.
      </Typography>

      <Card sx={{mb: 3}}>
        <CardContent>
          <Typography variant="subtitle1" component="h3" gutterBottom>
            Token claims
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleRefreshTokenClaims}
            disabled={refreshing || claimsLoading}
            sx={{mb: 2}}
          >
            {refreshing ? 'Refreshing…' : 'Refresh token claims'}
          </Button>
          {claimsLoading ? (
            <CircularProgress size={24} />
          ) : claims ? (
            <Typography
              component="pre"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                overflow: 'auto',
                p: 2,
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              {formatJson(claims)}
            </Typography>
          ) : (
            <Typography color="text.secondary">No claims (not signed in via token).</Typography>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" component="h3" gutterBottom>
            User record
          </Typography>
          <Typography
            component="pre"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              overflow: 'auto',
              p: 2,
              bgcolor: 'action.hover',
              borderRadius: 1,
            }}
          >
            {formatJson(user)}
          </Typography>
        </CardContent>
      </Card>
    </ContentContainer>
  );
}
